import MainLayout from "@/components/layout/MainLayout.vue";
import { usePermissions } from "@/composables/usePermissions";
import { useAuthStore } from "@/stores/auth";
import type { RouteRecordRaw } from "vue-router";
import { createRouter, createWebHistory } from "vue-router";

const routes: RouteRecordRaw[] = [
  {
    path: "/login",
    name: "Login",
    component: () => import("@/views/LoginView.vue"),
    meta: { requiresAuth: false },
  },
  {
    path: "/",
    component: MainLayout,
    meta: { requiresAuth: true },
    children: [
      {
        path: "",
        name: "Home",
        redirect: "/user-dashboard",
      },
      {
        path: "user-dashboard",
        name: "UserDashboard",
        component: () => import("@/views/UserDashboardView.vue"),
      },
      {
        path: "jobs",
        name: "Jobs",
        component: () => import("@/views/JobsView.vue"),
      },
      {
        path: "results",
        name: "Results",
        component: () => import("@/views/ResultsView.vue"),
      },
      {
        path: "price-movers",
        name: "PriceMovers",
        component: () => import("@/views/PriceMoversView.vue"),
      },
      {
        path: "favorites",
        name: "Favorites",
        component: () => import("@/views/ResultsView.vue"),
        props: {
          favoritesOnly: true,
          title: "Favorites",
          description: "Favorisierte Jobs mit Multi-Round-Ergebnissen",
        },
      },
      {
        path: "tags",
        name: "Tags",
        component: () => import("@/views/TagsView.vue"),
      },
      {
        path: "batch-lists",
        name: "BatchLists",
        component: () => import("@/views/BatchListsView.vue"),
      },
      {
        path: "batch-import",
        name: "BatchImport",
        component: () => import("@/views/BatchImportView.vue"),
      },
      {
        path: "shares",
        name: "Shares",
        component: () => import("@/views/SharesView.vue"),
      },
      {
        path: "exports",
        name: "Exports",
        component: () => import("@/views/ExportsView.vue"),
      },
      {
        path: "user-settings",
        name: "UserSettings",
        component: () => import("@/views/UserSettingsView.vue"),
      },
      {
        path: "notifications",
        name: "Notifications",
        component: () => import("@/views/NotificationsView.vue"),
      },
      {
        path: "admin/mappings",
        name: "AdminMappings",
        component: () => import("@/views/admin/MappingsView.vue"),
        meta: { requiresAdmin: true },
      },
      {
        path: "admin/users",
        name: "AdminUsers",
        component: () => import("@/views/admin/UsersView.vue"),
        meta: { requiresAdmin: true },
      },
      {
        path: "admin/roles",
        name: "AdminRoles",
        component: () => import("@/views/admin/RolesManagementView.vue"),
        meta: { requiresAdmin: true },
      },
      {
        path: "admin/variants",
        name: "AdminVariants",
        component: () => import("@/views/admin/VariantsView.vue"),
        meta: { requiresAdmin: true },
      },
      {
        path: "admin/round-configs",
        name: "AdminRoundConfigs",
        component: () => import("@/views/admin/RoundConfigsView.vue"),
        meta: { requiresAdmin: true },
      },
      {
        path: "admin/site-configs",
        name: "AdminSiteConfigs",
        component: () => import("@/views/AdminSiteConfigs.vue"),
        meta: {
          requiresAuth: true,
          requiredPermissions: ["siteConfigs.viewOwn", "siteConfigs.viewAll"],
        },
      },
      {
        path: "admin/dashboard",
        name: "AdminDashboard",
        component: () => import("@/views/AdminDashboardView.vue"),
        meta: {
          requiresAuth: true,
          requiredPermissions: ["adminDashboard.viewAll"],
        },
      },
      {
        path: "admin/analytics",
        name: "ScrapingCostsAnalytics",
        component: () => import("@/views/admin/ScrapingCostsAnalyticsView.vue"),
        meta: {
          requiresAuth: true,
          requiredPermissions: ["analytics.viewAll"],
        },
      },
      {
        path: "admin/price-stats-settings",
        name: "PriceStatsSettings",
        component: () => import("@/views/admin/PriceStatsSettingsView.vue"),
        meta: {
          requiresAuth: true,
          requiredPermissions: ["analytics.viewAll"],
        },
      },
      {
        path: "admin/audit-logs",
        name: "AuditLogs",
        component: () => import("@/views/AuditLogView.vue"),
        meta: { requiresAdmin: true },
      },
      {
        path: "admin/proxies",
        name: "ProxyManagement",
        component: () => import("@/views/admin/ProxyManagementView.vue"),
        meta: { requiresAdmin: true },
      },
      {
        path: "admin/scraper-configs",
        name: "ScraperConfigEditor",
        component: () => import("@/views/admin/ScraperConfigView.vue"),
        meta: { requiresAdmin: true },
      },
      {
        path: "admin/browser-profiles",
        name: "BrowserProfileManagement",
        component: () =>
          import("@/views/admin/BrowserProfileManagementView.vue"),
        meta: { requiresAdmin: true },
      },
      {
        path: "admin/proxy-settings",
        name: "ProxySettings",
        component: () => import("@/views/admin/ProxySettingsView.vue"),
        meta: { requiresAdmin: true },
      },
      {
        path: "admin/logs",
        name: "Logs",
        component: () => import("@/views/LogsView.vue"),
        meta: { requiresAdmin: true },
      },
    ],
  },
  {
    path: "/share/:id",
    name: "PublicShare",
    component: () => import("@/views/PublicShareView.vue"),
    meta: { requiresAuth: false },
  },
  {
    path: "/:pathMatch(.*)*",
    name: "NotFound",
    component: () => import("@/views/NotFoundView.vue"),
    meta: { requiresAuth: false },
  },
];

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,
  scrollBehavior() {
    // Prevent any scrolling on navigation
    return false;
  },
});

// Navigation guard for authentication and permissions
router.beforeEach((to, _from, next) => {
  const authStore = useAuthStore();
  const { canAny } = usePermissions();

  // Wait for initialization to complete before checking auth
  if (authStore.isInitializing) {
    // App is still initializing, wait for it to complete
    const unwatch = authStore.$subscribe(() => {
      if (!authStore.isInitializing) {
        unwatch();
        next(); // Re-trigger navigation after initialization
      }
    });
    return;
  }

  // Allow access to public routes without authentication check
  if (to.meta.requiresAuth === false) {
    next();
    return;
  }

  // Check authentication for protected routes
  if (!authStore.isAuthenticated) {
    next({ name: "Login", query: { redirect: to.fullPath } });
    return;
  }

  // Check admin access
  if (to.meta.requiresAdmin && !authStore.canAccessAdmin) {
    next({ name: "Jobs" });
    return;
  }

  // Check required permissions
  if (to.meta.requiredPermissions && Array.isArray(to.meta.requiredPermissions)) {
    const permissions = to.meta.requiredPermissions as string[];
    const hasPermission = canAny(permissions).value;
    if (!hasPermission) {
      next({ name: "Jobs" }); // Redirect to Jobs if no permission
      return;
    }
  }

  // Redirect if already logged in trying to access login
  if (to.name === "Login" && authStore.isAuthenticated) {
    next(authStore.canAccessAdmin ? { name: "Dashboard" } : { name: "Jobs" });
    return;
  }

  next();
});

export default router;
